/**
 * Systemadmin-UI for å administrere hjelpe-artikler.
 * - List, opprett, rediger, arkiver
 * - Markdown-forhåndsvisning
 * - Versjonshistorikk
 * - Forslag-kø fra brukere
 * - Analytics (siste 30 dager)
 */
import { useCallback, useEffect, useState } from 'react';
import {
  BookOpen, Plus, Search, FileEdit, Archive, Eye, EyeOff, Lightbulb, BarChart3,
  CheckCircle, XCircle, Save, X, History
} from 'lucide-react';
import {
  adminListArticles, adminGetArticle, adminCreateArticle, adminUpdateArticle,
  adminArchiveArticle, adminListSuggestions, adminPatchSuggestion, adminGetStats, adminGetHistory,
  categoryLabel,
  type HelpArticle, type HelpSuggestion, type HelpStats, type AdminArticlePayload,
  type HelpRoleScope, type HelpLanguage
} from '../../lib/help';
import MarkdownRenderer from '../help/MarkdownRenderer';

type Tab = 'articles' | 'suggestions' | 'stats';

const ROLE_LABELS: Record<HelpRoleScope, string> = {
  all: 'Alle innloggede',
  member: 'Medlem',
  member_admin: 'Medlem-administrator',
  admin: 'Studieforbund-admin',
  systemadmin: 'Systemadmin'
};

const CATEGORY_SUGGESTIONS = [
  'general', 'study-plan', 'exam-set', 'course-instance', 'lms',
  'assessment-framework', 'esco', 'system', 'rubric', 'accommodations'
];

export default function HelpArticlesManagement() {
  const [tab, setTab] = useState<Tab>('articles');
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showDrafts, setShowDrafts] = useState(true);
  const [filterLang, setFilterLang] = useState<HelpLanguage | ''>('');

  const [editing, setEditing] = useState<HelpArticle | null>(null);
  const [creating, setCreating] = useState(false);

  const [suggestions, setSuggestions] = useState<HelpSuggestion[]>([]);
  const [suggestionStatus, setSuggestionStatus] = useState<string>('pending');

  const [stats, setStats] = useState<HelpStats | null>(null);
  const [statsDays, setStatsDays] = useState(30);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListArticles({
        include_archived: showArchived,
        include_drafts: showDrafts,
        category: filterCategory || undefined,
        language: filterLang || undefined,
        q: query || undefined
      });
      setArticles(data.articles);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [showArchived, showDrafts, filterCategory, filterLang, query]);

  useEffect(() => {
    if (tab === 'articles') fetchArticles();
  }, [tab, fetchArticles]);

  useEffect(() => {
    if (tab === 'suggestions') {
      adminListSuggestions(suggestionStatus).then(setSuggestions).catch(() => setSuggestions([]));
    }
  }, [tab, suggestionStatus]);

  useEffect(() => {
    if (tab === 'stats') {
      adminGetStats(statsDays).then(setStats).catch(() => setStats(null));
    }
  }, [tab, statsDays]);

  const handleCreate = () => {
    setEditing(null);
    setCreating(true);
  };

  const handleEdit = async (a: HelpArticle) => {
    try {
      const full = await adminGetArticle(a.id);
      setEditing(full);
      setCreating(false);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleArchive = async (a: HelpArticle) => {
    if (!confirm(`Arkiver artikkel "${a.title}"? (kan ikke angres gjennom UI)`)) return;
    try {
      await adminArchiveArticle(a.id);
      fetchArticles();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-kf-gray-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-kf-blue-600" />
          Hjelpe-artikler
        </h1>
        <p className="text-sm text-kf-gray-600 mt-1">
          DB-drevet hjelpesystem. Rediger innhold uten kode-deploy. Artikler lenkes fra tooltips, panels og wizards.
        </p>
      </header>

      <nav className="border-b border-kf-gray-200 mb-6 flex gap-1 overflow-x-auto">
        {([
          ['articles', 'Artikler', FileEdit],
          ['suggestions', 'Forslag fra brukere', Lightbulb],
          ['stats', 'Statistikk', BarChart3]
        ] as const).map(([t, label, Icon]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 whitespace-nowrap border-b-2 transition-colors
              ${tab === t
                ? 'border-kf-blue-600 text-kf-blue-700'
                : 'border-transparent text-kf-gray-600 hover:text-kf-gray-900'}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 mb-4">
          {error}
        </div>
      )}

      {tab === 'articles' && (
        <>
          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-kf-gray-400" />
              <input
                type="text"
                placeholder="Søk tittel/slug"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="pl-8 pr-3 py-2 border border-kf-gray-300 rounded text-sm w-56"
              />
            </div>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-kf-gray-300 rounded text-sm"
            >
              <option value="">Alle kategorier</option>
              {CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filterLang}
              onChange={e => setFilterLang(e.target.value as HelpLanguage | '')}
              className="px-3 py-2 border border-kf-gray-300 rounded text-sm"
            >
              <option value="">Alle språk</option>
              <option value="nb">Norsk (nb)</option>
              <option value="en">Engelsk (en)</option>
            </select>
            <label className="flex items-center gap-1 text-sm text-kf-gray-700">
              <input type="checkbox" checked={showDrafts} onChange={e => setShowDrafts(e.target.checked)} />
              Vis utkast
            </label>
            <label className="flex items-center gap-1 text-sm text-kf-gray-700">
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
              Vis arkiverte
            </label>
            <div className="flex-1" />
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded text-sm font-semibold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Ny artikkel
            </button>
          </div>

          <div className="bg-white border border-kf-gray-200 rounded">
            {loading ? (
              <div className="p-6 text-sm text-kf-gray-500">Laster...</div>
            ) : articles.length === 0 ? (
              <div className="p-6 text-sm text-kf-gray-500">Ingen artikler matcher filteret.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-kf-gray-50 border-b border-kf-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Slug</th>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Tittel</th>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Kategori</th>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Rolle</th>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Språk/v</th>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-xs">Oppdatert</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map(a => (
                    <tr key={a.id} className="border-b border-kf-gray-100 hover:bg-kf-gray-50">
                      <td className="px-3 py-2 font-mono text-xs text-kf-gray-700">{a.slug}</td>
                      <td className="px-3 py-2 text-kf-gray-900">{a.title}</td>
                      <td className="px-3 py-2 text-xs text-kf-gray-600" title={a.category}>{categoryLabel(a.category)}</td>
                      <td className="px-3 py-2 text-xs text-kf-gray-600">{ROLE_LABELS[a.role_scope]}</td>
                      <td className="px-3 py-2 text-xs text-kf-gray-600">{a.language} / v{a.version}</td>
                      <td className="px-3 py-2 text-xs">
                        {a.is_archived ? (
                          <span className="inline-flex items-center gap-1 text-kf-gray-500"><Archive className="w-3 h-3" /> Arkivert</span>
                        ) : a.is_draft ? (
                          <span className="inline-flex items-center gap-1 text-amber-700"><EyeOff className="w-3 h-3" /> Utkast</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="w-3 h-3" /> Publisert</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-kf-gray-500">{new Date(a.updated_at).toLocaleDateString('nb-NO')}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleEdit(a)}
                          className="text-kf-blue-700 hover:underline text-xs mr-2"
                        >
                          Rediger
                        </button>
                        {!a.is_archived && (
                          <button
                            onClick={() => handleArchive(a)}
                            className="text-red-700 hover:underline text-xs"
                          >
                            Arkiver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === 'suggestions' && (
        <div>
          <div className="flex gap-2 mb-4">
            {(['pending', 'in_progress', 'resolved', 'dismissed'] as const).map(s => (
              <button
                key={s}
                onClick={() => setSuggestionStatus(s)}
                className={`px-3 py-1.5 text-sm rounded ${suggestionStatus === s ? 'bg-kf-blue-600 text-white' : 'bg-kf-gray-100 text-kf-gray-700 hover:bg-kf-gray-200'}`}
              >
                {s === 'pending' ? 'Ventende' : s === 'in_progress' ? 'Pågår' : s === 'resolved' ? 'Løst' : 'Avvist'}
              </button>
            ))}
          </div>
          <div className="bg-white border border-kf-gray-200 rounded">
            {suggestions.length === 0 ? (
              <div className="p-6 text-sm text-kf-gray-500">Ingen forslag med status "{suggestionStatus}".</div>
            ) : (
              <ul className="divide-y divide-kf-gray-100">
                {suggestions.map(s => (
                  <li key={s.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-kf-gray-500 mb-1">
                          {s.suggester_name || 'Anonym'} ({s.suggested_role}) · {new Date(s.created_at).toLocaleString('nb-NO')}
                        </div>
                        <div className="text-xs text-kf-gray-500 mb-1">
                          Rute: <code className="bg-kf-gray-100 px-1 rounded">{s.route}</code>
                        </div>
                        <p className="text-sm text-kf-gray-800">{s.user_message}</p>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {suggestionStatus === 'pending' && (
                          <>
                            <button
                              onClick={async () => {
                                const slug = prompt('Slug på artikkelen som løser dette (valgfritt):');
                                await adminPatchSuggestion(s.id, 'resolved', slug || undefined);
                                adminListSuggestions(suggestionStatus).then(setSuggestions);
                              }}
                              className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Marker løst
                            </button>
                            <button
                              onClick={async () => {
                                await adminPatchSuggestion(s.id, 'dismissed');
                                adminListSuggestions(suggestionStatus).then(setSuggestions);
                              }}
                              className="text-xs text-red-700 hover:underline flex items-center gap-1"
                            >
                              <XCircle className="w-3 h-3" />
                              Avvis
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'stats' && (
        <div>
          <div className="flex gap-2 mb-4 items-center">
            <span className="text-sm text-kf-gray-700">Periode:</span>
            {[7, 30, 90, 365].map(d => (
              <button
                key={d}
                onClick={() => setStatsDays(d)}
                className={`px-3 py-1 text-sm rounded ${statsDays === d ? 'bg-kf-blue-600 text-white' : 'bg-kf-gray-100 hover:bg-kf-gray-200'}`}
              >
                {d === 365 ? '1 år' : `${d} d`}
              </button>
            ))}
          </div>
          {stats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Publiserte" value={stats.counts.published} />
                <StatCard label="Utkast" value={stats.counts.drafts} />
                <StatCard label="Arkivert" value={stats.counts.archived} />
                <StatCard label="Ventende forslag" value={stats.counts.pending_suggestions} tone="amber" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-kf-gray-900 mb-2">Mest leste artikler</h3>
                  <div className="bg-white border border-kf-gray-200 rounded">
                    {stats.top_articles.length === 0 ? (
                      <div className="p-3 text-xs text-kf-gray-500">Ingen views ennå.</div>
                    ) : (
                      <ul className="divide-y divide-kf-gray-100 text-sm">
                        {stats.top_articles.slice(0, 15).map(a => (
                          <li key={a.article_slug} className="flex justify-between items-center px-3 py-1.5">
                            <code className="text-xs">{a.article_slug}</code>
                            <span className="text-xs text-kf-gray-500">{a.views}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-kf-gray-900 mb-2">Mest aktive ruter</h3>
                  <div className="bg-white border border-kf-gray-200 rounded">
                    {stats.top_routes.length === 0 ? (
                      <div className="p-3 text-xs text-kf-gray-500">Ingen rute-tracking ennå.</div>
                    ) : (
                      <ul className="divide-y divide-kf-gray-100 text-sm">
                        {stats.top_routes.slice(0, 15).map(r => (
                          <li key={r.route} className="flex justify-between items-center px-3 py-1.5">
                            <code className="text-xs truncate max-w-[60%]">{r.route}</code>
                            <span className="text-xs text-kf-gray-500">{r.views}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-kf-gray-500">Laster statistikk...</div>
          )}
        </div>
      )}

      {(editing || creating) && (
        <ArticleEditor
          article={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); fetchArticles(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'blue' }: { label: string; value: number; tone?: 'blue' | 'amber' }) {
  const colors = tone === 'amber'
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-kf-blue-50 border-kf-blue-200 text-kf-blue-900';
  return (
    <div className={`border rounded p-3 ${colors}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Article editor
// ---------------------------------------------------------------------------

function ArticleEditor({
  article,
  onClose,
  onSaved
}: {
  article: HelpArticle | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !article;
  const [form, setForm] = useState<AdminArticlePayload & { bump_version?: boolean }>({
    slug: article?.slug ?? '',
    language: article?.language ?? 'nb',
    title: article?.title ?? '',
    summary: article?.summary ?? '',
    body_markdown: article?.body_markdown ?? '',
    role_scope: article?.role_scope ?? 'all',
    context_routes: article?.context_routes ?? [],
    category: article?.category ?? 'general',
    tags: article?.tags ?? [],
    is_draft: article?.is_draft ?? false,
    bump_version: false
  });
  const [routeInput, setRouteInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HelpArticle[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (article) {
      adminGetHistory(article.slug).then(setHistory).catch(() => setHistory([]));
    }
  }, [article]);

  const addRoute = () => {
    const r = routeInput.trim();
    if (!r) return;
    try {
      new RegExp(r);
    } catch {
      setError(`Ugyldig regex: ${r}`);
      return;
    }
    setForm(f => ({ ...f, context_routes: [...(f.context_routes || []), r] }));
    setRouteInput('');
  };

  const removeRoute = (idx: number) => {
    setForm(f => ({ ...f, context_routes: (f.context_routes || []).filter((_, i) => i !== idx) }));
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setForm(f => ({ ...f, tags: [...(f.tags || []), t] }));
    setTagInput('');
  };

  const removeTag = (idx: number) => {
    setForm(f => ({ ...f, tags: (f.tags || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await adminCreateArticle(form);
      } else if (article) {
        await adminUpdateArticle(article.id, form);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9000] flex items-start justify-center bg-kf-gray-900/40 overflow-y-auto p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-5xl my-8"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-kf-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-kf-gray-900">
            {isNew ? 'Ny hjelpe-artikkel' : `Rediger: ${article?.title}`}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(p => !p)}
              className="px-3 py-1.5 text-xs bg-kf-gray-100 hover:bg-kf-gray-200 rounded"
            >
              {showPreview ? 'Skjul forhåndsvisning' : 'Forhåndsvisning'}
            </button>
            <button onClick={onClose} className="p-1 text-kf-gray-500 hover:text-kf-gray-900">
              <X className="w-5 h-5" />
            </button>
          </div>
        </header>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-6 px-6 py-4`}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Slug" required>
                <input
                  value={form.slug}
                  onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                  disabled={!isNew}
                  placeholder="eqf-nkr-level"
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm font-mono disabled:bg-kf-gray-50"
                />
              </Field>
              <Field label="Språk" required>
                <select
                  value={form.language}
                  onChange={e => setForm(f => ({ ...f, language: e.target.value as HelpLanguage }))}
                  disabled={!isNew}
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm disabled:bg-kf-gray-50"
                >
                  <option value="nb">Norsk</option>
                  <option value="en">Engelsk</option>
                </select>
              </Field>
            </div>

            <Field label="Tittel" required>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Hva er EQF/NKR-nivå?"
                className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm"
              />
            </Field>

            <Field label={`Sammendrag (10-500 tegn, ${form.summary?.length ?? 0})`} required>
              <textarea
                value={form.summary}
                onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
                rows={2}
                placeholder="Kort oppsummering som vises i tooltip. En setning som gjør det tydelig hva artikkelen handler om."
                className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm"
              />
            </Field>

            <Field label={`Innhold — Markdown (20-50000 tegn, ${form.body_markdown?.length ?? 0})`} required>
              <textarea
                value={form.body_markdown}
                onChange={e => setForm(f => ({ ...f, body_markdown: e.target.value }))}
                rows={14}
                placeholder="# Overskrift&#10;&#10;Markdown-innhold. Støttes: lister, tabeller, lenker, kode, sitater."
                className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm font-mono"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Rolle">
                <select
                  value={form.role_scope}
                  onChange={e => setForm(f => ({ ...f, role_scope: e.target.value as HelpRoleScope }))}
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm"
                >
                  {(Object.keys(ROLE_LABELS) as HelpRoleScope[]).map(r =>
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  )}
                </select>
              </Field>
              <Field label="Kategori">
                <input
                  list="help-categories"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded text-sm"
                />
                <datalist id="help-categories">
                  {CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c} />)}
                </datalist>
              </Field>
            </div>

            <Field label="Kontekst-ruter (regex, matcher mot gjeldende rute)">
              <div className="flex gap-2 mb-2">
                <input
                  value={routeInput}
                  onChange={e => setRouteInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRoute(); } }}
                  placeholder="^/study-plans/.*"
                  className="flex-1 px-3 py-1.5 border border-kf-gray-300 rounded text-sm font-mono"
                />
                <button onClick={addRoute} className="px-3 py-1.5 bg-kf-blue-600 text-white rounded text-xs">Legg til</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {(form.context_routes || []).map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-kf-gray-100 rounded text-xs font-mono">
                    {r}
                    <button onClick={() => removeRoute(i)} className="text-kf-gray-500 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </Field>

            <Field label="Tags">
              <div className="flex gap-2 mb-2">
                <input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="eqf, vurderingsramme"
                  className="flex-1 px-3 py-1.5 border border-kf-gray-300 rounded text-sm"
                />
                <button onClick={addTag} className="px-3 py-1.5 bg-kf-blue-600 text-white rounded text-xs">Legg til</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {(form.tags || []).map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded text-xs">
                    #{t}
                    <button onClick={() => removeTag(i)} className="text-kf-gray-500 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </Field>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!form.is_draft}
                onChange={e => setForm(f => ({ ...f, is_draft: e.target.checked }))}
              />
              Lagre som utkast (ikke synlig for brukere)
            </label>

            {!isNew && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.bump_version}
                  onChange={e => setForm(f => ({ ...f, bump_version: e.target.checked }))}
                />
                Opprett ny versjon (arkiverer gjeldende)
              </label>
            )}

            {!isNew && history.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-kf-gray-600 flex items-center gap-1">
                  <History className="w-3 h-3" />
                  Versjonshistorikk ({history.length})
                </summary>
                <ul className="mt-2 border border-kf-gray-200 rounded">
                  {history.map(h => (
                    <li key={h.id} className="px-2 py-1 border-b last:border-b-0 border-kf-gray-100 flex gap-2 text-xs">
                      <span className="font-mono">v{h.version}</span>
                      <span>{h.language}</span>
                      <span className="flex-1 truncate">{h.title}</span>
                      {h.is_archived ? <span className="text-kf-gray-500">arkivert</span> :
                       h.is_draft ? <span className="text-amber-700">utkast</span> :
                       <span className="text-emerald-700">aktiv</span>}
                      <span className="text-kf-gray-500">{new Date(h.updated_at).toLocaleDateString('nb-NO')}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {showPreview && (
            <div className="border-l border-kf-gray-200 pl-6">
              <h3 className="text-xs uppercase tracking-wide text-kf-gray-500 font-semibold mb-2">Forhåndsvisning</h3>
              <h4 className="text-lg font-semibold text-kf-gray-900 mb-2">{form.title || '(uten tittel)'}</h4>
              {form.summary && (
                <p className="text-sm text-kf-gray-700 italic border-l-4 border-kf-orange-300 pl-3 mb-4">
                  {form.summary}
                </p>
              )}
              <MarkdownRenderer content={form.body_markdown || '*(tom)*'} />
            </div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-kf-gray-200 flex justify-end gap-2 bg-kf-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-kf-gray-100 hover:bg-kf-gray-200 rounded"
          >
            Avbryt
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Lagrer...' : (isNew ? 'Opprett' : (form.bump_version ? 'Lagre ny versjon' : 'Lagre'))}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label, required, children
}: {
  label: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-kf-gray-700 flex items-center gap-1">
        {label}{required && <span className="text-red-500">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
